import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Tooltip,
  Dropdown,
  OverlayTrigger,
} from "react-bootstrap";
import { useSiteContext } from "./Providers/SSCProvider";
// import NewExportSiteCircuitsCSV from "./NewExportSiteCircuitsCSV";
import SiteInfo from "./SiteInfo";
import Icon from "../generic/Icon";
import ActionButtons from "./ActionButtons";
import Filtering from "./Filtering";
import { useRoleContext } from "./Providers/RoleProvider";
import ExportSiteCircuitsCSV from "./ExportSiteCircuitsCSV";
import CarrierContactCard from "./CarrierContactCard";
import AddTempSiteModal from "./Modals/AddTempSiteModal";

const SiteSearch = () => {
  const {
    isLoading,
    globalSitesData,
    selectedSite,
    setSelectedSite,
    filteredSites,
    inputValue,
    setInputValue,
    isDropdownVisible,
    setIsDropdownVisible,
    handleSelectSite,
    filtering,
    setFiltering,
    selectedCarrierInfo,
    filteredStructures,
    filteredCircuits,
    filteredTempSites,
    filteredTempStructures,
    includeEmpty,
    setIncludeEmpty,
    handleSelectStructure,
    handleSelectCircuit,
  } = useSiteContext();
  const { isAdmin } = useRoleContext();
  const selectedSiteRef = useRef(selectedSite);
  const dropdownRef = useRef(null);
  const formRef = useRef(null);

  const exportRef = useRef(null);
  const inputBoxRef = useRef(null);

  const [tempSiteModal, setTempSiteModal] = useState({
    show: false,
    name: "",
    country: "",
    city: "",
  });

  let charCount = 1024;

  useEffect(() => {
    selectedSiteRef.current = selectedSite;
  }, [selectedSite]);

  const handleChange = (e) => {
    console.log(e.target.value);
    setInputValue(e.target.value);
    setIsDropdownVisible(e.target.value.length >= 3);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Implement submit logic if needed
  };

  const handleExport = (isAdmin, dataToExport) => {
    if (exportRef.current) {
      exportRef.current.exportToCSV(isAdmin, dataToExport);
    }
  };

  const addTempSite = () => {
    setSelectedSite("");
    setFiltering(false);
    setIsDropdownVisible(false);
    setTempSiteModal({ show: true, name: inputValue });
  };

  const handleClickOutside = useCallback(
    (event) => {
      const currentSite = selectedSiteRef.current;
      const trimmedInput = inputValue.trim();

      if (
        !dropdownRef.current &&
        inputValue === "" &&
        currentSite &&
        !inputBoxRef.current.contains(event.target)
      ) {
        setInputValue(
          `${currentSite.id} - ${currentSite.name} - ${currentSite.city}`,
        );
      } else if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownVisible(false);
        if (!currentSite && filteredSites.length === 0) {
          setInputValue("");
        } else if (currentSite && currentSite.siteId && trimmedInput === "") {
          setInputValue(
            `${currentSite.id} - ${currentSite.name} - ${currentSite.city}`,
          );
        }
      }
    },
    [
      inputValue,
      filteredSites?.length,
      setInputValue,
      setIsDropdownVisible,
      dropdownRef,
      inputBoxRef,
      selectedSiteRef,
    ],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const sortedFilteredSites = useMemo(
    () => [...filteredSites].sort((a, b) => a.siteId - b.siteId),
    [filteredSites],
  );

  return (
    <>
      <Row className="justify-content-center">
        {!selectedSite && <Col sm={2} />}
        <Col
          className="pe-0"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <Row>
            <Form onSubmit={handleSubmit} ref={formRef}>
              <Form.Group className="position-relative">
                <div className="d-flex align-items-center w-100">
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id="tooltip-view-site-details">
                        Search by Site/Structure Details or Circuit Name -
                        (Enter at least 3 characters)
                      </Tooltip>
                    }
                  >
                    <Form.Control
                      disabled={isLoading}
                      type="text"
                      placeholder="Search..."
                      value={inputValue}
                      onChange={handleChange}
                      className="flex-grow-1"
                    />
                  </OverlayTrigger>
                  <ExportSiteCircuitsCSV ref={exportRef} />
                  {!selectedSite && (
                    <Icon
                      name="export"
                      onClick={() => handleExport(isAdmin, globalSitesData)}
                      overrides={{
                        disabled: isLoading,
                        tooltip: "Export Site Details",
                        tooltipPlacement: "top",
                        style: { marginLeft: "10px" },
                      }}
                    />
                  )}
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id="tooltip-include-empty">
                        {/* When unchecked, sites and structures without circuits
                        are hidden */}
                        Include Empty Sites & Structures in Search Results
                      </Tooltip>
                    }
                  >
                    <div>
                      <Form.Check
                        label="Include Empty"
                        checked={includeEmpty}
                        onChange={() => setIncludeEmpty((prev) => !prev)}
                        className="ms-2"
                        style={{ whiteSpace: "nowrap" }}
                      />
                    </div>
                  </OverlayTrigger>
                </div>

                {isDropdownVisible && (
                  <Card
                    ref={dropdownRef}
                    className="position-absolute w-100"
                    style={{ top: "100%", zIndex: 999 }}
                  >
                    <Dropdown
                      className="text-left"
                      style={{ maxHeight: "360px", overflowY: "auto" }}
                    >
                      {filteredSites.length > 0 && (
                        <>
                          <i>Sites:</i>
                          {sortedFilteredSites.map((site) => (
                            <Dropdown.Item
                              key={site.siteId}
                              className="p-0 dropdown-item-hover w-100 text-truncate"
                              onClick={() => handleSelectSite(site.siteId)}
                            >
                              {site.siteId} - {site.siteName} -{" "}
                              <i>({site.city})</i>
                            </Dropdown.Item>
                          ))}
                        </>
                      )}
                      {filteredTempSites.length > 0 && (
                        <>
                          <i>Temp Sites:</i>
                          {filteredTempSites.map((site) => (
                            <Dropdown.Item
                              key={site.siteId}
                              className="p-0 dropdown-item-hover w-100 text-truncate"
                              onClick={() => handleSelectSite(site.siteId)}
                            >
                              {site.siteId} - {site.siteName} -{" "}
                              <i>({site.city})</i>
                            </Dropdown.Item>
                          ))}
                        </>
                      )}
                      {/* Structures */}
                      {filteredStructures.length > 0 && (
                        <>
                          <i>Structures:</i>
                          {filteredStructures.map((structure) => (
                            <Dropdown.Item
                              key={structure.structureId}
                              className="p-0 dropdown-item-hover w-100 text-truncate"
                              onClick={() => {
                                handleSelectStructure(structure.structureId);
                              }}
                            >
                              {structure.structureId} -{" "}
                              {structure.structureName} -{" "}
                              <i>({structure.structureCity})</i>
                            </Dropdown.Item>
                          ))}
                        </>
                      )}
                      {filteredTempStructures.length > 0 && (
                        <>
                          <i>Temp Structures:</i>
                          {filteredTempStructures.map((structure) => (
                            <Dropdown.Item
                              key={structure.structureId}
                              className="p-0 dropdown-item-hover w-100 text-truncate"
                              onClick={() => {
                                handleSelectStructure(structure.structureId);
                              }}
                            >
                              {structure.structureId} -{" "}
                              {structure.structureName} -{" "}
                              <i>({structure.structureCity})</i>
                            </Dropdown.Item>
                          ))}
                        </>
                      )}
                      {/* Circuits */}
                      {filteredCircuits.length > 0 && (
                        <>
                          <i>Circuits:</i>
                          {filteredCircuits.map((circuit) => (
                            <Dropdown.Item
                              key={circuit.circuitGmId}
                              className="p-0 dropdown-item-hover w-100 text-truncate"
                              onClick={() => {
                                handleSelectCircuit(circuit.circuitGmId);
                              }}
                            >
                              {circuit.circuitVendorId} -{" "}
                              <i>(Structure {circuit.structureId})</i>
                            </Dropdown.Item>
                          ))}
                        </>
                      )}
                      {/* Admin add temp site */}
                      {isAdmin && (
                        <div
                          style={{
                            padding: "5px",
                            cursor: "pointer",
                            borderTop: "1px solid #ccc",
                            textAlign: "center",
                            fontWeight: "bold",
                            backgroundColor: "#0072ce",
                            color: "white",
                            whiteSpace: "pre",
                          }}
                          onClick={addTempSite}
                        >
                          Create Temp Site: {inputValue}
                        </div>
                      )}
                    </Dropdown>
                  </Card>
                )}
              </Form.Group>
            </Form>
          </Row>
          {selectedSite && (
            <>
              <SiteInfo />
              <ActionButtons />
            </>
          )}
        </Col>

        {selectedSite ? (
          <Col className="">
            <Form style={{ height: "100%" }}>
              <Form.Group
                className="mb-0 position-relative"
                controlId="description"
                style={{ height: "100%" }}
              >
                <Form.Control
                  as="textarea"
                  placeholder="Free form notes for Site / Circuits"
                  name="description"
                  disabled={!selectedSite || !isAdmin}
                  value={selectedSite.note ?? ""}
                  onChange={(e) =>
                    isAdmin &&
                    setSelectedSite((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                  }
                  maxLength={1024}
                  // style={getInputStyle({
                  //   height: "100%",
                  //   overflow: "auto",
                  //   resize: "none",
                  // })}
                  style={{
                    height: "100%",
                    overflow: "auto",
                    resize: "none",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    right: "8px",
                    fontSize: "10px",
                    marginTop: "2px",
                    color: "green",
                  }}
                >
                  {selectedSite?.note
                    ? charCount - selectedSite.note.length
                    : charCount}{" "}
                  Characters Remaining
                </span>
              </Form.Group>
            </Form>
          </Col>
        ) : (
          <Col sm={2} />
        )}
        {selectedCarrierInfo && <CarrierContactCard />}
      </Row>
      {filtering && <Filtering />}
      <AddTempSiteModal
        modalData={tempSiteModal}
        setModalData={setTempSiteModal}
      />
    </>
  );
};

export default SiteSearch;
